/* ------------------------------------------------------------------ *
 * Booking card — presentation data
 *
 * Tone for each booking status, and the backdrop each vehicle class is
 * photographed against.
 * ------------------------------------------------------------------ */

export type StatusTone = "live" | "ok" | "warn" | "off" | "bad";

/* The card prints the status exactly as the API reports it. What is mapped
   here is only its tone, so the colour still says at a glance whether a
   journey is running, settled or gone wrong even where the word itself is
   operations vocabulary. */
const STATUS_TONE: Record<string, StatusTone> = {
  posted: "ok",
  accepted: "live",
  ongoing: "live",
  started: "live",
  resumed: "live",
  arrived: "live",
  paused: "live",
  finished: "off",
  cancelled: "bad",
  noshow: "bad",
};

/** Active trip — same user-facing label as accepted (Chauffeur assigned). */
const ACTIVE_TRIP_STATUS_KEYS = new Set([
  "accepted",
  "ongoing",
  "started",
  "arrived",
  "paused",
  "resumed",
]);

export const SETTLED_BOOKING_STATUS_KEYS = new Set([
  "finished",
  "cancelled",
  "noshow",
]);

export function normalizeBookingStatusKey(status?: string): string {
  return String(status ?? "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");
}

export function statusPresentation(status?: string): { tone: StatusTone } {
  const key = normalizeBookingStatusKey(status);
  return { tone: STATUS_TONE[key] ?? "ok" };
}

/** Upcoming tab: everything except settled history and pending checkout. */
export function isUpcomingBookingStatus(status?: string): boolean {
  const key = normalizeBookingStatusKey(status);
  if (!key || key === "pending") return false;
  return !SETTLED_BOOKING_STATUS_KEYS.has(key);
}

/** User-facing label (posted → Confirmed; active trip states → Chauffeur assigned). */
export function bookingStatusLabel(
  status: string | undefined,
  t: (key: string, options?: { defaultValue?: string }) => string
): string {
  const key = normalizeBookingStatusKey(status);
  if (!key) return "";
  if (key === "posted") {
    return t("posted", { defaultValue: "Confirmed" });
  }
  if (ACTIVE_TRIP_STATUS_KEYS.has(key)) {
    return t("accepted", { defaultValue: "Chauffeur assigned" });
  }
  return t(key, { defaultValue: String(status ?? "") });
}

/* ------------------------------------------------------------------ *
 * Vehicle stage
 *
 * Each card opens on a lit stage holding the car, the way the class is
 * shown on the selection screen — the photograph is what makes a booking
 * recognisable in a list at a glance, long before the reference number is.
 * The backdrop is chosen from the class name so a class always looks the
 * same wherever it appears, and so new classes added on the backend still
 * get a considered backdrop rather than a default grey.
 * ------------------------------------------------------------------ */

export interface StageLook {
  background: string;
}

/* Yelo's own light purple, not the neutral studio greys the reference used.
   Each class gets its own reading of it so a class looks the same wherever
   it appears and a new class added on the backend still lands on a
   considered backdrop rather than a default grey. */
const STAGE_SEDAN: StageLook = {
  background: "linear-gradient(165deg, #EFE8F3 0%, #FBF8FC 54%, #DFD2E8 100%)",
};
const STAGE_LUX: StageLook = {
  background: "linear-gradient(165deg, #EADFF1 0%, #FAF6FC 52%, #D6C4E2 100%)",
};
const STAGE_SUV: StageLook = {
  background: "linear-gradient(165deg, #EDE7F2 0%, #FAF7FC 55%, #DACFE4 100%)",
};
const STAGE_VAN: StageLook = {
  background: "linear-gradient(165deg, #E9E6F2 0%, #F8F6FC 55%, #D3CEE6 100%)",
};

export function stageLook(className?: string): StageLook {
  const name = String(className ?? "").toLowerCase();
  if (name.includes("van") || name.includes("mpv")) return STAGE_VAN;
  if (name.includes("suv")) return STAGE_SUV;
  if (name.includes("first") || name.includes("premium")) return STAGE_LUX;
  return STAGE_SEDAN;
}
